import { ComponentConfig } from "@measured/puck"
import { Button, message, Modal, Select } from "antd"
import { useEffect, useState } from "react"
import { MenuApiResponse } from "~/types/types"
import { t } from "../../../../../../languages/i18n"
import menuService from "../../../../../menu/service/menuService"
import { configs, DefaultImage, Media, MediaUpload } from "../../../../external-components"
import { NavigationProps, RenderConfig } from "./RenderConfig"

const SelectCard = ({
  menuItems,
  onSelectMenu,
}: {
  menuItems: MenuApiResponse[]
  onSelectMenu: (menuName: string) => void
}) => {
  return (
    <div className="flex flex-col gap-4">
      <Select placeholder="Select Menu" style={{ width: "100%" }} onChange={onSelectMenu}>
        {menuItems.map(item => (
          <Select.Option key={item.id} value={item.attributes.name}>
            {item.attributes.name}
          </Select.Option>
        ))}
      </Select>
    </div>
  )
}

const ImageCard = ({ value, onChange }: any) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isChooseMedia, setIsChooseMedia] = useState(true)
  const [url, setUrl] = useState(value)

  const openModal = () => {
    setIsModalOpen(true)
  }

  const handleSelectMedia = (media: { url: string }) => {
    setUrl(media.url)
    onChange(media.url)
    setIsModalOpen(false)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  return (
    <div>
      <div className="flex flex-col gap-2">
        <input type="text" value={url} onChange={e => onChange(e.target.value)} placeholder="Logo URL" readOnly />
        <div className="relative cursor-pointer group" onClick={() => openModal()}>
          <img
            src={url ? `${configs.API_URL}${url}` : DefaultImage}
            alt="Logo"
            className="rounded-lg max-h-96 cursor-pointer transition duration-300 ease-in-out group-hover:blur-sm overflow-hidden"
          />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300 ease-in-out">
            <span className="text-white text-3xl font-semibold drop-shadow-md">Change media</span>
          </div>
        </div>

        <Button
          onClick={() => openModal()}
          type="default"
          variant="outlined"
          className="bg-blue text-white p-4 border-[1px] border-blue rounded-[3px] hover:bg-blue-800 hover:text-white duration-300 my-2"
        >
          Select Image
        </Button>
      </div>

      <Modal
        open={isModalOpen}
        title={<span className="ml-4">Select Media</span>}
        onCancel={handleCloseModal}
        style={{ top: 20 }}
        width="95%"
        footer={null}
      >
        <div className="ml-4 mt-5">
          <Button
            onClick={() => setIsChooseMedia(true)}
            className="mr-2"
            style={{
              backgroundColor: isChooseMedia ? "blue" : "initial",
              color: isChooseMedia ? "white" : "initial",
            }}
          >
            Select Media
          </Button>
          <Button
            onClick={() => setIsChooseMedia(false)}
            style={{
              backgroundColor: !isChooseMedia ? "blue" : "initial",
              color: !isChooseMedia ? "white" : "initial",
            }}
          >
            Upload Media
          </Button>
        </div>

        <div>
          {isChooseMedia ? (
            <Media isOpenModal={true} onSelectMedia={handleSelectMedia} />
          ) : (
            <MediaUpload isOpenModal={true} setChooseMedia={setIsChooseMedia} />
          )}
        </div>
      </Modal>
    </div>
  )
}

export const SKENavigation: ComponentConfig<NavigationProps> = {
  label: "SKE | Navigation",
  fields: {
    url: {
      label: "Logo URL",
      type: "custom",
      render: props => <ImageCard {...props} />,
    },
    menu: {
      label: "Select Menu",
      type: "custom",
      render: ({ onChange }) => {
        const [menuItems, setMenuItems] = useState<MenuApiResponse[]>([])

        useEffect(() => {
          const fetchMenuItems = async () => {
            try {
              const response = await menuService.fetchAllMenuPreference()
              setMenuItems(response.data)
            } catch (error) {
              message.error(t("Error fetching menu preferences. Please try again."))
            }
          }
          fetchMenuItems()
        }, [])

        return <SelectCard menuItems={menuItems} onSelectMenu={(menuName: string) => onChange(menuName)} />
      },
    },
    slogan: {
      label: "Slogan",
      type: "text",
    },
    logoStyles: {
      label: "Logo Styles",
      type: "object",
      objectFields: {
        width: {
          label: "Width",
          type: "text",
        },
        height: {
          label: "Height",
          type: "text",
        },
        padding: {
          label: "Padding",
          type: "text",
        },
        margin: {
          label: "Margin",
          type: "text",
        },
        objectFit: {
          label: "Object Fit",
          type: "select",
          options: [
            { label: "Cover", value: "cover" },
            { label: "Contain", value: "contain" },
            { label: "Fill", value: "fill" },
            { label: "None", value: "none" },
            { label: "Scale Down", value: "scale-down" },
          ],
        },
      },
    },
    sloganStyles: {
      label: "Slogan Styles",
      type: "object",
      objectFields: {
        fontSize: {
          label: "Font Size",
          type: "text",
        },
        fontWeight: {
          label: "Font Weight",
          type: "text",
        },
        color: {
          label: "Color",
          type: "text",
        },
        margin: {
          label: "Margin ",
          type: "text",
        },
        padding: {
          label: "Padding ",
          type: "text",
        },
      },
    },

    type: {
      label: "Layout Type",
      type: "select",
      options: [
        { label: "Fluid", value: "fluid" },
        { label: "Custom", value: "custom" },
        { label: "Boxed", value: "boxed" },
      ],
    },
    className: {
      label: "Class Name",
      type: "text",
    },
  },
  ...RenderConfig,
}
